/**
 * Pipeline Registry — Heart of Science v2
 * Central configuration for 12 pipeline stages
 * Drives pipeline UI, filtering, and workflow
 * Last Updated: 2026-04-03
 */

import {
  PIPELINE_STAGE_CODES,
  ALLOWED_TRANSITIONS as SHARED_ALLOWED_TRANSITIONS,
  type PipelineStage as SharedPipelineStage,
} from '@hos/shared/constants'

// Re-export PipelineStage type from shared
export type PipelineStage = SharedPipelineStage

// S9-05: MAPPED removed per CEO decision 2026-04-07
// Note: web-specific configuration (labels, colors, etc.) stays here
// but stage codes are now imported from shared
const PIPELINE_STAGES_CONFIG = [
  {
    code: 'INBOX',
    label: 'Inbox',
    color: '#6B7280',
    tailwindColor: 'pipeline-inbox',
    order: 0,
    description: 'Initial inbox, not yet reviewed',
  },
  {
    code: 'LONG_LIST',
    label: 'Long List',
    color: '#3B82F6',
    tailwindColor: 'pipeline-long-list',
    order: 1,
    description: 'Passed initial screening',
  },
  {
    code: 'PICKED_FOR_REVIEW',
    label: 'Picked for Review',
    color: '#818CF8',
    tailwindColor: 'pipeline-picked-for-review',
    order: 2,
    description: 'Selected from long list for detailed review',
  },
  {
    code: 'IN_REVIEW',
    label: 'In Review',
    color: '#F59E0B',
    tailwindColor: 'pipeline-in-review',
    order: 3,
    description: 'Under detailed review',
  },
  {
    code: 'TO_PRESENT',
    label: 'To Present',
    color: '#EC4899',
    tailwindColor: 'pipeline-to-present',
    order: 4,
    description: 'Review complete, ready to present to team',
  },
  {
    code: 'EXTRA_MILE',
    label: 'Extra Mile',
    color: '#F97316',
    tailwindColor: 'pipeline-extra-mile',
    order: 5,
    description: 'Deep dive analysis phase',
  },
  {
    code: 'VB_PROCESS',
    label: 'VB Process',
    color: '#10B981',
    tailwindColor: 'pipeline-vb-process',
    order: 6,
    description: 'Active venture building process',
  },
  {
    code: 'FUNDED',
    label: 'Funded',
    color: '#059669',
    tailwindColor: 'pipeline-funded',
    order: 7,
    description: 'Project received funding',
  },
  {
    code: 'EXITED',
    label: 'Exited',
    color: '#8B5CF6',
    tailwindColor: 'pipeline-exited',
    order: 8,
    description: 'Successful exit or acquisition',
  },
  {
    code: 'PARKED',
    label: 'Parked',
    color: '#94A3B8',
    tailwindColor: 'pipeline-parked',
    order: 9,
    description: 'On hold or deprioritized',
  },
  {
    code: 'GRAVEYARD',
    label: 'Graveyard',
    color: '#EF4444',
    tailwindColor: 'pipeline-graveyard',
    order: 10,
    description: 'Rejected or closed',
  },
] as const

export interface PipelineConfig {
  code: PipelineStage
  label: string
  color: string
  tailwindColor: string
  order: number
  description: string
}

// Re-export PIPELINE_STAGES for backward compatibility
export const PIPELINE_STAGES = PIPELINE_STAGES_CONFIG

/**
 * Map for fast lookup by stage code
 */
export const PIPELINE_MAP: Record<PipelineStage, PipelineConfig> = Object.fromEntries(
  PIPELINE_STAGES_CONFIG.map(stage => [stage.code, stage]),
) as Record<PipelineStage, PipelineConfig>

/**
 * Pre-built Tailwind class maps
 * Avoids dynamic interpolation which won't survive Tailwind purge
 * Usage: className={PIPELINE_BADGE_CLASSES[stage]}
 */

export const PIPELINE_BADGE_CLASSES: Record<PipelineStage, string> = {
  INBOX: 'bg-[#6B72801a] text-[#6B7280]',
  LONG_LIST: 'bg-[#3B82F61a] text-[#3B82F6]',
  PICKED_FOR_REVIEW: 'bg-[#818CF81a] text-[#818CF8]',
  IN_REVIEW: 'bg-[#F59E0B1a] text-[#F59E0B]',
  TO_PRESENT: 'bg-[#EC48991a] text-[#EC4899]',
  EXTRA_MILE: 'bg-[#F973161a] text-[#F97316]',
  VB_PROCESS: 'bg-[#10B9811a] text-[#10B981]',
  FUNDED: 'bg-[#0596691a] text-[#059669]',
  EXITED: 'bg-[#8B5CF61a] text-[#8B5CF6]',
  PARKED: 'bg-[#94A3B81a] text-[#94A3B8]',
  GRAVEYARD: 'bg-[#EF44441a] text-[#EF4444]',
}

export const PIPELINE_CHIP_ACTIVE_CLASSES: Record<PipelineStage, string> = {
  INBOX: 'bg-pipeline-inbox/20 text-pipeline-inbox border-pipeline-inbox/30',
  LONG_LIST: 'bg-pipeline-long-list/20 text-pipeline-long-list border-pipeline-long-list/30',
  PICKED_FOR_REVIEW: 'bg-pipeline-picked-for-review/20 text-pipeline-picked-for-review border-pipeline-picked-for-review/30',
  IN_REVIEW: 'bg-pipeline-in-review/20 text-pipeline-in-review border-pipeline-in-review/30',
  TO_PRESENT: 'bg-pipeline-to-present/20 text-pipeline-to-present border-pipeline-to-present/30',
  EXTRA_MILE: 'bg-pipeline-extra-mile/20 text-pipeline-extra-mile border-pipeline-extra-mile/30',
  VB_PROCESS: 'bg-pipeline-vb-process/20 text-pipeline-vb-process border-pipeline-vb-process/30',
  FUNDED: 'bg-pipeline-funded/20 text-pipeline-funded border-pipeline-funded/30',
  EXITED: 'bg-pipeline-exited/20 text-pipeline-exited border-pipeline-exited/30',
  PARKED: 'bg-pipeline-parked/20 text-pipeline-parked border-pipeline-parked/30',
  GRAVEYARD: 'bg-pipeline-graveyard/20 text-pipeline-graveyard border-pipeline-graveyard/30',
}

export const PIPELINE_BADGE_DARK_CLASSES: Record<PipelineStage, string> = {
  INBOX: 'bg-pipeline-inbox text-text-inverse',
  LONG_LIST: 'bg-pipeline-long-list text-text-inverse',
  PICKED_FOR_REVIEW: 'bg-pipeline-picked-for-review text-text-inverse',
  IN_REVIEW: 'bg-pipeline-in-review text-text-inverse',
  TO_PRESENT: 'bg-pipeline-to-present text-text-inverse',
  EXTRA_MILE: 'bg-pipeline-extra-mile text-text-inverse',
  VB_PROCESS: 'bg-pipeline-vb-process text-text-inverse',
  FUNDED: 'bg-pipeline-funded text-text-inverse',
  EXITED: 'bg-pipeline-exited text-text-inverse',
  PARKED: 'bg-pipeline-parked text-text-inverse',
  GRAVEYARD: 'bg-pipeline-graveyard text-text-inverse',
}

export const PIPELINE_DOT_CLASSES: Record<PipelineStage, string> = {
  INBOX: 'bg-pipeline-inbox',
  LONG_LIST: 'bg-pipeline-long-list',
  PICKED_FOR_REVIEW: 'bg-pipeline-picked-for-review',
  IN_REVIEW: 'bg-pipeline-in-review',
  TO_PRESENT: 'bg-pipeline-to-present',
  EXTRA_MILE: 'bg-pipeline-extra-mile',
  VB_PROCESS: 'bg-pipeline-vb-process',
  FUNDED: 'bg-pipeline-funded',
  EXITED: 'bg-pipeline-exited',
  PARKED: 'bg-pipeline-parked',
  GRAVEYARD: 'bg-pipeline-graveyard',
}

// Re-export ALLOWED_TRANSITIONS from shared (single source of truth)
export const ALLOWED_TRANSITIONS = SHARED_ALLOWED_TRANSITIONS

/**
 * Check if a transition from one stage to another is allowed
 */
export function isTransitionAllowed(from: PipelineStage, to: PipelineStage): boolean {
  return ALLOWED_TRANSITIONS[from]?.includes(to) ?? false
}

/**
 * Helper to get stage config by code
 */
export function getPipelineStageConfig(code: string | PipelineStage): PipelineConfig | undefined {
  return PIPELINE_MAP[code as PipelineStage]
}

/**
 * Helper to get all stages sorted by order
 */
export function getPipelineStageSorted(): PipelineConfig[] {
  return [...PIPELINE_STAGES].sort((a, b) => a.order - b.order)
}
